import {stringify} from "comment-json";
import {omit, startCase} from "lodash";
import {NodeId, NodeType} from "../../../../model/Graph";
import {Playshot} from "../../../../model/Playshot";
import {makeAsyncIterable} from "../../../async-iterable/make-async-iterable";
import {CodyResponse, CodyResponseType} from "../../../Cody";
import {getNodeByIdOrFQCN} from "../../../cody-wizard/node/get-node-by-id-or-fqcn";
import {nodeLabel, removeDefaultService} from "../../default-service";
import {ProophBoardDescriptionKeys} from "../../descriptions";
import {
  PlayshotApplyContext,
  PlayshotChanged,
  PlayshotChangedDiff,
  PlayshotChangedOperation,
  PlayshotChangedType
} from "../PlayshotChanged";
import {PageDefinition} from "./page-definitions";

export class PageMetadataChanged implements PlayshotChanged {
  public readonly name: string;
  public readonly type: PlayshotChangedType = 'PageMetadataChanged';
  public readonly operation: PlayshotChangedOperation = 'changed';
  public readonly nodeId: NodeId;
  public readonly nodeType = NodeType.ui;
  public readonly diff: PlayshotChangedDiff;

  private changedPage: PageDefinition;

  constructor(name: string, nodeId: string, page: PageDefinition, newPlayshot: Playshot, oldPlayshot: Playshot) {
    let oldDefinition = oldPlayshot.playConfig.pages[name];

    if(!oldDefinition) {
      oldDefinition = page;
    }

    this.name = name;
    this.nodeId = nodeId;
    this.diff = {
      new: page,
      old: oldDefinition,
    };
    this.changedPage = page;
  }

  public async apply({syncContext, graph, playshot, addedNodes, elementsTree}: PlayshotApplyContext): Promise<AsyncIterable<CodyResponse>> {
    if(addedNodes[this.nodeId]) {
      return makeAsyncIterable({
        cody: ``,
        type: CodyResponseType.Empty
      })
    }

    const existingNode = getNodeByIdOrFQCN(this.nodeId, this.name, NodeType.ui, graph, elementsTree);

    if(!existingNode) {
      return makeAsyncIterable({
        cody: `Hhm, I'm skipping the change "${startCase(this.type)}: ${removeDefaultService(this.name, playshot)}", because the page cannot be found on the board.`,
        details: `There is no element on the board with ID: ${this.nodeId}`,
        type: CodyResponseType.Warning
      })
    }

    graph.changeNodeMetadata(existingNode, stringify(omit({...this.changedPage, views: this.changedPage.components}, [
      "name",
      "topLevel",
      "components",
      ...ProophBoardDescriptionKeys
    ])))


    graph.focusNode(existingNode);


    return makeAsyncIterable({
      cody: `I've updated metadata of ui card "${nodeLabel(this.name)}".`
    });
  }
}
